import useFeedbackRequests from "../function/hook/useFeedbackRequests";
import TaxGuruDialog from "../../../components/TaxGuruDialog";
import { useForm } from "react-hook-form";
import useDialogState from "../../../components/hook/useDialogState";
import { CoreUserFeedback } from "@mm-products/java-multima-framework";
import React from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { TextareaAutosizeElement } from "react-hook-form-mui";
import * as yup from "yup";
import { yupResolver } from "../../../utils/yupUtils";
import { Button } from "@mui/material";
import { cancelFeedback, createDirectFeedback, resolveFeedback } from "../../../backend/taxguru/client/jmf/coreFeedbackClient";
import RatingElement from "../../../components/form/RatingElement";
import target = CoreUserFeedback.target;

const feedbackSchema = yup
    .object({
        rating: yup.number(),
        message: yup.string(),
    })
    .test("at-least-one", function (value) {
        const valueRatingEmpty = value.rating !== undefined && value.rating === 0;
        const messageEmpty = (value.message?.trim()?.length ?? 0) === 0;
        return !(valueRatingEmpty && messageEmpty);
    });

export default function FeedbackDialog() {
    const { t } = useTranslation();
    const { onFeedbackDone, feedbacks } = useFeedbackRequests();
    const { open, onOpen, onClose } = useDialogState();
    const [feedback, setFeedback] = React.useState<CoreUserFeedback>();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const { control, formState, handleSubmit, reset } = useForm<{ rating?: number; message?: string }>({
        resolver: yupResolver(feedbackSchema),
        defaultValues: {
            rating: 0,
        },
    });

    // set first feedback on feedback change
    React.useEffect(() => {
        if (feedbacks.length > 0) {
            setFeedback(feedbacks[0]);
            onOpen();
        } else {
            onClose();
        }
    }, [feedbacks, onClose, onOpen]);
    // reset form on new feedback
    React.useEffect(() => {
        reset();
    }, [feedback, reset]);

    if (!feedback) {
        return null;
    }

    const onSendFeedback = () => {
        handleSubmit(async (data) => {
            setIsSubmitting(true);
            try {
                if (feedback?.id) {
                    await resolveFeedback(feedback.id, { rating: data.rating, userMessage: data.message });
                } else {
                    await createDirectFeedback({ rating: data.rating, userMessage: data.message, source: target.WEB });
                }
                onFeedbackDone(feedback);
            } finally {
                setIsSubmitting(false);
            }
        })();
    };
    const onCancelFeedback = async () => {
        if (feedback?.id) {
            setIsSubmitting(true);
            try {
                await cancelFeedback(feedback.id);
            } finally {
                setIsSubmitting(false);
            }
        }
        onFeedbackDone(feedback);
    };

    const hasErrors = Object.keys(formState.errors).length > 0;
    return (
        <TaxGuruDialog disableClosing open={open} variant="forForm">
            <Stack alignItems="center" spacing={4}>
                <Typography textAlign="center" variant="h5">
                    {feedback.description}
                </Typography>
                <RatingElement name="rating" control={control} />
                <TextareaAutosizeElement rows={5} fullWidth label={t("feedback.message")} name="message" control={control} />
                <Stack alignSelf="stretch" direction="row" justifyContent={hasErrors ? "space-between" : "flex-end"} spacing={2}>
                    {hasErrors && (
                        <Typography variant="caption" color="error">
                            {t("validation.atLeastOneRequired")}
                        </Typography>
                    )}
                    <Stack sx={{ ml: "auto" }} direction="row" spacing={1}>
                        <Button disabled={isSubmitting} variant="contained" color="secondary" onClick={onCancelFeedback}>
                            {t("feedback.cancel")}
                        </Button>
                        <Button disabled={isSubmitting} variant="contained" color="primary" onClick={onSendFeedback}>
                            {t("feedback.confirm")}
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </TaxGuruDialog>
    );
}
